import whitelabelApi from "../services/http/whitelabelApi";
import { getTenantSlug } from "../services/http/tenant";
import { localStorageKeys } from "./localStorageKeys";

/**
 * Marca, contato, tema e links do parceiro. Antes vinham de `VITE_*` no build
 * de cada parceiro; agora vêm da API (`GET /config/web`), que lê do start-admin
 * e guarda uma cópia para quando o admin estiver fora.
 *
 * Ordem por campo: API → última resposta guardada neste navegador (se a API
 * falhar) → env do build. O env só faz sentido no build de um parceiro fora do
 * multi-tenant; no build multi-tenant ele não deve ter marca.
 *
 * Também traz o comportamento do webapp que varia por parceiro (limites dos
 * relatórios, dias de busca, tela de código), lido via `webSettings`.
 */
export type TenantConfig = {
  source: "admin" | "fallback" | "env" | "cache" | "build";
  name: string | null;
  companyName: string | null;
  cnpj: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  theme: Record<string, string> | null;
  cdnUrl: string | null;
  webUrl: string | null;
  inviteUrl: string | null;
  termsUrl: string | null;
  validationUrl: string | null;
  appGoogleUrl: string | null;
  appAppleUrl: string | null;
  teamsAppGoogleUrl: string | null;
  teamsAppAppleUrl: string | null;
  couponUrl: string | null;
  logoSimulationUrl: string | null;
  reportsFetchAmount: number | null;
  cpfsScheduleAmount: number | null;
  transactionSearchDays: number | null;
  codeScreen: boolean | null;
};

type Field = Exclude<keyof TenantConfig, "source">;

const FIRST_LOAD_TIMEOUT_MS = 10000;
const REFRESH_TIMEOUT_MS = 10000;

const env = (value: unknown): string | null => {
  const text = typeof value === "string" ? value.trim() : "";
  return text || null;
};

const envNumber = (value: unknown): number | null => {
  const number = Number(value);
  return value && Number.isFinite(number) && number > 0 ? number : null;
};

const envBoolean = (value: unknown): boolean | null =>
  value === "true" ? true : value === "false" ? false : null;

const parseTheme = (raw: unknown): Record<string, string> | null => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

function fromBuildEnv(): TenantConfig {
  const e = import.meta.env;

  return {
    source: "build",
    name: env(e.VITE_COMPANY),
    companyName: env(e.VITE_COMPANY_PJNAME),
    cnpj: env(e.VITE_COMPANY_CNPJ),
    email: env(e.VITE_COMPANY_EMAIL),
    phone: env(e.VITE_COMPANY_PHONE),
    address: env(e.VITE_COMPANY_ADDRESS),
    logoUrl: env(e.VITE_LOGO),
    faviconUrl: env(e.VITE_FAVICON),
    theme: parseTheme(e.VITE_THEME),
    cdnUrl: env(e.VITE_CDN_URL),
    webUrl: null,
    inviteUrl: env(e.VITE_TEAMS_URL),
    termsUrl: env(e.VITE_TERMOS_URL),
    validationUrl: env(e.VITE_VALIDACAO_URL),
    appGoogleUrl: env(e.VITE_GOOGLE_URL),
    appAppleUrl: env(e.VITE_APPLE_URL),
    teamsAppGoogleUrl: env(e.VITE_TEAMS_GOOGLE_URL),
    teamsAppAppleUrl: env(e.VITE_TEAMS_APPLE_URL),
    couponUrl: env(e.VITE_COUPON),
    logoSimulationUrl: env(e.VITE_LOGO_SIMULATION),
    reportsFetchAmount: envNumber(e.VITE_AMOUNT_REPORTS_FETCH),
    cpfsScheduleAmount: envNumber(e.VITE_AMOUNT_CPFS_SCHEDULE),
    transactionSearchDays: envNumber(e.VITE_TRANSACTION_SEARCH_DAYS),
    codeScreen: envBoolean(e.VITE_CODE_SCREEN),
  };
}

/** Primeiro valor preenchido, campo a campo. */
function merge(
  ...layers: Array<Partial<TenantConfig> | null | undefined>
): TenantConfig {
  const present = layers.filter(Boolean) as Partial<TenantConfig>[];
  const result = { ...fromBuildEnv() };
  const fields = Object.keys(result).filter((k) => k !== "source") as Field[];

  for (const field of fields) {
    const layer = present.find((l) => {
      const value = l[field];
      return value !== null && value !== undefined && value !== "";
    });
    if (layer) (result as Record<Field, unknown>)[field] = layer[field];
  }
  result.source = present[0]?.source ?? "build";

  return result;
}

let current: TenantConfig = fromBuildEnv();

/** Config do parceiro já carregada (síncrono; `main.tsx` carrega antes de importar o App). */
export function tenantConfig(): TenantConfig {
  return current;
}

/**
 * Acesso em string, para uso direto em JSX/concatenação: campo vazio vira ''
 * (como era com o env ausente).
 */
export const brand = {
  get name() {
    return current.name ?? "";
  },
  get companyName() {
    return current.companyName ?? "";
  },
  get cnpj() {
    return current.cnpj ?? "";
  },
  get email() {
    return current.email ?? "";
  },
  get phone() {
    return current.phone ?? "";
  },
  get address() {
    return current.address ?? "";
  },
  get logoUrl() {
    return current.logoUrl ?? "";
  },
  get faviconUrl() {
    return current.faviconUrl ?? "";
  },
  get cdnUrl() {
    return current.cdnUrl ?? "";
  },
  get inviteUrl() {
    return current.inviteUrl ?? "";
  },
  get termsUrl() {
    return current.termsUrl ?? "";
  },
  get validationUrl() {
    return current.validationUrl ?? "";
  },
  get appGoogleUrl() {
    return current.appGoogleUrl ?? "";
  },
  get appAppleUrl() {
    return current.appAppleUrl ?? "";
  },
  get teamsAppGoogleUrl() {
    return current.teamsAppGoogleUrl ?? "";
  },
  get teamsAppAppleUrl() {
    return current.teamsAppAppleUrl ?? "";
  },
  get couponUrl() {
    return current.couponUrl ?? "";
  },
  get logoSimulationUrl() {
    return current.logoSimulationUrl ?? "";
  },
};

/** Comportamento do webapp por parceiro, com o padrão de quando nada veio. */
export const webSettings = {
  get reportsFetchAmount() {
    return current.reportsFetchAmount ?? 1000;
  },
  get cpfsScheduleAmount() {
    return current.cpfsScheduleAmount ?? 500;
  },
  get transactionSearchDays() {
    return current.transactionSearchDays ?? 60;
  },
  get codeScreen() {
    return current.codeScreen ?? false;
  },
};

/** URL de validação de identidade de um usuário (a base pode vir com ou sem `/`). */
export function validationUrlFor(id: string): string {
  return `${brand.validationUrl.replace(/\/+$/, "")}/${id}`;
}

const cacheKey = () =>
  `${localStorageKeys.TENANT_CONFIG}:${getTenantSlug() ?? "default"}`;

function readCache(): TenantConfig | null {
  try {
    const raw = localStorage.getItem(cacheKey());
    return raw ? { ...JSON.parse(raw), source: "cache" } : null;
  } catch {
    return null;
  }
}

function writeCache(config: TenantConfig) {
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(config));
  } catch {
    // armazenamento indisponível (modo privado, cota): segue sem cache
  }
}

/**
 * Base da API do whitelabel escolhido na tela de código, se houver. A config
 * ainda não chegou neste ponto: decide pela última guardada ou pelo env.
 */
function whitelabelBaseUrl(): string | undefined {
  if (!(readCache()?.codeScreen ?? fromBuildEnv().codeScreen)) return undefined;
  try {
    const raw = localStorage.getItem(localStorageKeys.WHITELABEL);
    return raw ? JSON.parse(raw)?.url : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Aplica a última config guardada neste navegador, se houver. Devolve `true`
 * quando aplicou: aí o app pode abrir na hora e `refreshTenantConfig` roda em
 * segundo plano.
 */
export function applyCachedTenantConfig(): boolean {
  const cached = readCache();
  if (!cached) return false;

  current = merge(cached);
  applyTenantConfig(current);
  return true;
}

/**
 * Busca a config na API e guarda a resposta. Se falhar, mantém o que já está
 * aplicado (cache) ou cai no env do build. O que já foi lido pelos módulos
 * segue com o valor anterior até o próximo carregamento da página.
 */
export async function refreshTenantConfig({
  background = false,
}: { background?: boolean } = {}): Promise<TenantConfig> {
  try {
    const { data } = await whitelabelApi.get<TenantConfig>("/config/web", {
      timeout: background ? REFRESH_TIMEOUT_MS : FIRST_LOAD_TIMEOUT_MS,
      baseURL: whitelabelBaseUrl(),
    });
    writeCache(data);
    current = merge(data);
  } catch (error) {
    console.error("Falha ao carregar a config do parceiro", error);
    if (!background) current = merge(readCache());
  }

  applyTenantConfig(current);

  return current;
}

// ---------------------------------------------------------------------------
// Tema, título e favicon em runtime (antes: gerados no build a partir do env)
// ---------------------------------------------------------------------------

const THEME_VARS: Record<string, string[]> = {
  background: ["--background"],
  foreground: ["--foreground"],
  primary: ["--primary", "--color-primary"],
  primaryForeground: ["--primary-foreground"],
  secondary: ["--secondary"],
  secondaryForeground: ["--secondary-foreground"],
};

/** `#rrggbb` → `h s% l%`, o formato que o `hsl(var(--x))` do tema espera. */
export function toHslTriplet(value: string): string {
  const hex = value.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
  if (!hex) return value.trim();

  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(full.slice(i, i + 2), 16) / 255,
  );
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }

  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

export function applyTenantConfig(config: TenantConfig) {
  const root = document.documentElement;

  for (const [key, value] of Object.entries(config.theme ?? {})) {
    if (typeof value !== "string" || !value.trim()) continue;
    for (const cssVar of THEME_VARS[key] ?? []) {
      root.style.setProperty(cssVar, toHslTriplet(value));
    }
  }

  if (config.name) document.title = config.name;

  const favicon = config.faviconUrl;
  if (favicon) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.removeAttribute("type");
    link.href = favicon;
  }
}
